import { Injectable } from '@angular/core';
import { CognitoService } from 'src/app/services/cognito.service';
import { environment } from 'src/environments/environment';
import { CollabraService } from './collabra.service';
import {
  AgentRunTriggeredEvent,
  DocumentUpdatedEvent,
  TaskCreatedEvent,
  TaskDeletedEvent,
  TaskUpdatedEvent,
  UserAccessRevokedEvent,
  UserInvitedEvent,
  UserRoleChangedEvent,
  WorkspaceArchivedEvent,
  WorkspaceDeletedEvent,
  WorkspaceSocketSubscribeMessage,
  WorkspaceUpdatedEvent,
  WorkspaceWebsocketEvents
} from '@workspaces/models/websocket';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { WorkspaceAgentFetch } from '@workspaces/store/agent-status.feature';
import { WorkspaceFetch, WorkspaceListFetch } from '@workspaces/store/workspace-list.feature';
import { TaskListFetch } from '@workspaces/store/task-list.feature';
import { TaskDetailsFetch } from '@workspaces/store/task-details.feature';
import { UserManagementFetch } from '@workspaces/store/user-management.feature';

@Injectable()
export class WorkspaceSocketService {
  private _websocket: WebSocket | null = null;
  private subscribedWorkspaceIds: string[] = [];
  private currentlyOpenedWorkspaceId: string | null = '';
  private messageQueue: WorkspaceSocketSubscribeMessage[] = [];
  constructor(
    private readonly collabraService: CollabraService,
    private readonly cognitoService: CognitoService,
    private readonly snackbar: MatSnackBar,
    private readonly router: Router,
    private readonly store: Store<AppState>
  ) {
    this.initWebsocket();
  }
  private get isWebSocketOpen(): boolean {
    return this._websocket?.readyState === WebSocket.OPEN;
  }

  public get websocket(): WebSocket {
    if (!this._websocket) {
      this._websocket = this.initWebsocket();
    }
    return this._websocket;
  }

  private initWebsocket(): WebSocket {
    const idToken = this.cognitoService.authenticationSubject.getValue()?.getIdToken()?.getJwtToken();
    if (!idToken) {
      throw new Error('idToken was not found. Cannot initialize WebSocket.');
    }

    const wsUri = `${environment.wsUriWithoutToken}${idToken}`;
    const ws = new WebSocket(wsUri);

    ws.addEventListener('open', this.handleWsOpen.bind(this));
    ws.addEventListener('message', this.parseWsMessages.bind(this));
    ws.addEventListener('close', this.handleWsClose.bind(this));
    ws.addEventListener('error', this.handleWsError.bind(this));

    this._websocket = ws;
    return ws;
  }

  public setCurrentlyOpenedWorkspace(workspaceId: string | null) {
    this.currentlyOpenedWorkspaceId = workspaceId;
  }

  private handleWsOpen(): void {
    this.messageQueue.forEach(message => {
      this.sendMessage(message);
    });
    this.messageQueue = [];
  }

  private handleWsClose(): void {
    this._websocket = null;
  }

  private handleWsError(event: Event): void {
    console.error('WebSocket error:', event);
  }

  private sendMessage(message: WorkspaceSocketSubscribeMessage): void {
    if (this.isWebSocketOpen) {
      this._websocket!.send(JSON.stringify(message));
    } else {
      this.messageQueue.push(message);
    }
  }

  public subscribeToWorkspace(workspaceId: string): void {
    if (!workspaceId) {
      return;
    }

    if (this.subscribedWorkspaceIds.includes(workspaceId) && this.isWebSocketOpen) {
      return;
    }

    const subscribeMessage: WorkspaceSocketSubscribeMessage = {
      action: 'workspace_subscribe',
      workspace_id: workspaceId
    };

    this.sendMessage(subscribeMessage);

    if (!this.subscribedWorkspaceIds.includes(workspaceId)) {
      this.subscribedWorkspaceIds.push(workspaceId);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseWsMessages(ev: MessageEvent<any>): void {
    const stringData = ev.data;
    if (typeof stringData === 'string') {
      try {
        const parsedData = JSON.parse(stringData) as WorkspaceWebsocketEvents;

        this.handleEvent(parsedData);
      } catch (e) {
        console.error('Error parsing WebSocket message:', e, stringData);
      }
    }
  }
  private handleEvent(event: WorkspaceWebsocketEvents): void {
    switch (event.type) {
      case 'document_updated':
        this.handleDocumentUpdated(event);
        break;
      case 'agent_run_triggered':
        this.handleAgentRunTriggered(event);
        break;
      case 'workspace_updated':
        this.handleWorkspaceUpdated(event);
        break;
      case 'workspace_archived':
        this.handleWorkspaceArchived(event);
        break;
      case 'workspace_deleted':
        this.handleWorkspaceDeleted(event);
        break;
      case 'task_created':
        this.handleTaskCreated(event);
        break;
      case 'task_updated':
        this.handleTaskUpdated(event);
        break;
      case 'task_deleted':
        this.handleTaskDeleted(event);
        break;
      case 'user_invited':
        this.handleUserInvited(event);
        break;
      case 'user_access_revoked':
        this.handleUserAccessRevoked(event);
        break;
      case 'user_role_changed':
        this.handleUserRoleChanged(event);
        break;
      default:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        console.warn(`[Event Handler] Unknown event type received: ${(event as any).type}`);
        break;
    }
  }
  private handleDocumentUpdated(event: DocumentUpdatedEvent): void {
    if (this.currentlyOpenedWorkspaceId === event.workspace_id) {
      this.collabraService.refreshCollabra();
      this.store.dispatch(WorkspaceAgentFetch({ workspaceId: event.workspace_id }));
      this.snackbar.open('Document is updating!', 'Dismiss', {
        duration: 3000
      });
    }
  }

  private handleAgentRunTriggered(event: AgentRunTriggeredEvent): void {
    this.store.dispatch(WorkspaceAgentFetch({ workspaceId: event.workspace_id }));
  }

  private handleWorkspaceUpdated(event: WorkspaceUpdatedEvent): void {
    this.store.dispatch(WorkspaceFetch({ workspaceId: event.workspace_id }));
  }

  private handleWorkspaceArchived(event: WorkspaceArchivedEvent): void {
    this.store.dispatch(WorkspaceFetch({ workspaceId: event.workspace_id }));
  }

  private async handleWorkspaceDeleted(event: WorkspaceDeletedEvent): Promise<void> {
    const isAdmin = await this.cognitoService.isAdmin();
    this.store.dispatch(WorkspaceListFetch({ include_archived: true, include_deleted: isAdmin }));
    if (this.currentlyOpenedWorkspaceId === event.workspace_id) {
      this.router.navigate(['/workspaces']);
      this.snackbar.open('Workspace was deleted!', 'Dismiss', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  private handleTaskCreated(event: TaskCreatedEvent): void {
    this.store.dispatch(TaskListFetch({ workspaceId: event.workspace_id }));
  }

  private handleTaskUpdated(event: TaskUpdatedEvent): void {
    this.store.dispatch(TaskDetailsFetch({ workspaceId: event.workspace_id, taskId: event.task_id }));
  }

  private handleTaskDeleted(event: TaskDeletedEvent): void {
    this.store.dispatch(TaskListFetch({ workspaceId: event.workspace_id }));
  }

  private handleUserInvited(event: UserInvitedEvent): void {
    this.store.dispatch(UserManagementFetch({ workspaceId: event.workspace_id }));
  }

  private async handleUserAccessRevoked(event: UserAccessRevokedEvent): Promise<void> {
    this.store.dispatch(WorkspaceFetch({ workspaceId: event.workspace_id }));
    const userId = await this.cognitoService.getUserId();

    if (userId === event.user_id && this.currentlyOpenedWorkspaceId === event.workspace_id) {
      this.router.navigate(['/workspaces']);
      this.snackbar.open('Permission was revoked', 'Dismiss', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  private handleUserRoleChanged(event: UserRoleChangedEvent): void {
    this.store.dispatch(UserManagementFetch({ workspaceId: event.workspace_id }));
    this.store.dispatch(WorkspaceFetch({ workspaceId: event.workspace_id }));
  }
}
